import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Empty } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MessageSquare, Loader2, Send, CheckCircle2 } from "lucide-react";

const ROLE_COLOR = { client: "#002EA6", candidate: "#F5A623", internal: "#10B981" };

export default function AdminCommunications() {
  const { user } = useAuth();
  const [threads, setThreads] = useState(null);
  const [active, setActive] = useState(null);
  const [reply, setReply] = useState("");

  const load = () => api.get("/update-requests").then(({ data }) => {
    setThreads(data);
    if (active) { const a = data.find((t) => t.id === active.id); }
  });
  useEffect(() => { load(); }, []);

  const openThread = async (t) => { const { data } = await api.get(`/update-requests/${t.id}`); setActive(data); };
  const sendReply = async () => {
    if (!reply.trim()) return;
    const { data } = await api.post(`/update-requests/${active.id}/messages`, { body: reply });
    setActive(data); setReply(""); load();
  };
  const resolve = async () => {
    const next = active.status === "resolved" ? "open" : "resolved";
    const { data } = await api.patch(`/update-requests/${active.id}`, { status: next });
    setActive(data); load();
  };

  return (
    <div>
      <PageHeader overline="Update requests inbox" title="Communications" />
      <div className="p-8">
        {!threads && <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>}
        {threads && threads.length === 0 && <Empty icon={MessageSquare} title="Inbox empty" hint="Update requests from clients and candidates will land here." />}
        {threads && threads.length > 0 && (
          <div className="grid lg:grid-cols-3 gap-px bg-black/10 border border-black/10">
            <div className="bg-white max-h-[72vh] overflow-y-auto">
              {threads.map((t) => (
                <button key={t.id} onClick={() => openThread(t)} data-testid={`thread-${t.id}`}
                  className={`w-full text-left p-4 border-b border-black/5 hover:bg-black/5 transition-colors ${active?.id === t.id ? "bg-black/5" : ""}`}>
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium truncate pr-2">{t.subject}</p>
                    {t.status === "resolved" && <CheckCircle2 className="h-3.5 w-3.5 text-[#10B981] shrink-0" />}
                  </div>
                  <p className="overline text-[0.5rem] mt-1 flex items-center gap-1">
                    <span className="h-1.5 w-1.5 rounded-full" style={{ background: ROLE_COLOR[t.requester_role] || "#71717A" }} />
                    {t.requester_name || t.requester_role} · {t.messages?.length || 0} msgs
                  </p>
                </button>
              ))}
            </div>
            <div className="lg:col-span-2 bg-white p-6 flex flex-col min-h-[50vh]">
              {!active ? <p className="text-sm text-muted-foreground">Select a request.</p> : (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="font-heading font-bold text-lg">{active.subject}</p>
                      <p className="overline text-[0.55rem] mt-1">from {active.requester_name} ({active.requester_role})</p>
                    </div>
                    <Button size="sm" variant="ghost" onClick={resolve} data-testid="resolve-btn" className="rounded-none border border-black/10">
                      {active.status === "resolved" ? "Reopen" : "Mark resolved"}
                    </Button>
                  </div>
                  <div className="flex-1 space-y-3 overflow-y-auto">
                    {active.messages?.map((m, i) => {
                      const mine = m.sender_role === "internal";
                      return (
                        <div key={i} className={`max-w-[80%] p-3 text-sm ${mine ? "ml-auto bg-primary text-white" : "bg-[#F1F1F3]"}`}>
                          <p className="overline text-[0.5rem] mb-1" style={{ color: mine ? "rgba(255,255,255,0.7)" : undefined }}>{m.sender_name || m.sender_role}</p>
                          {m.body}
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex gap-2 mt-4 border-t border-black/10 pt-4">
                    <Input value={reply} onChange={(e) => setReply(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendReply()} placeholder="Reply to requester…" className="bg-[#F4F4F5] border-black/10 rounded-none" data-testid="admin-reply-input" />
                    <Button onClick={sendReply} data-testid="admin-reply-send" className="rounded-none bg-primary hover:bg-[#09090B]"><Send className="h-4 w-4" /></Button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
